import { useState, useEffect, useCallback } from 'react';

export interface CarouselImage {
  src: string;
  alt: string;
}

interface HeroCarouselProps {
  images?: CarouselImage[];
  interval?: number;
  paused?: boolean;
}

const DEFAULT_IMAGES: CarouselImage[] = [
  {
    src: '/images/hero/west-highland-way.jpg',
    alt: 'Hikers on the West Highland Way in Scotland with misty mountains',
  },
  {
    src: '/images/hero/alps-trekking.jpg',
    alt: 'Alpine trekking in the Swiss Alps with snow-capped peaks',
  },
  {
    src: '/images/hero/lake-district.jpg',
    alt: 'Lake District landscape with rolling hills and clear waters',
  },
];

export function HeroCarousel({
  images = DEFAULT_IMAGES,
  interval = 5000,
  paused = false,
}: HeroCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const cycleToNext = useCallback(() => {
    setActiveIndex((current) => (current + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (paused) {
      return;
    }

    const timerId = setInterval(cycleToNext, interval);

    return () => {
      clearInterval(timerId);
    };
  }, [cycleToNext, interval, paused]);

  return (
    <div
      data-testid="hero-carousel"
      className="relative h-64 sm:h-80 md:h-96 lg:h-[500px] w-full rounded-lg overflow-hidden"
    >
      {images.map((image, index) => {
        const isActive = index === activeIndex;
        return (
          <img
            key={image.src}
            src={image.src}
            alt={image.alt}
            data-active={isActive ? 'true' : 'false'}
            aria-hidden={!isActive}
            loading={isActive ? 'eager' : 'lazy'}
            className={`
              absolute inset-0 w-full h-full object-cover
              transition-opacity duration-1000 ease-in-out
              ${isActive ? 'opacity-100' : 'opacity-0'}
            `}
          />
        );
      })}
      <div
        data-testid="hero-carousel-overlay"
        className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none"
      />
    </div>
  );
}
